import { Requirement, Select, CourseSet, CourseSetSummary } from "../types/requirement.response";
import { CourseLite, CourseSummary, RequirementBindingReason } from "../types/course.response";
import { sortCourses, getRandomKey } from "./sortUtils";

export const unappliedReasonLabel: Record<RequirementBindingReason, { title: string; description: string }> = {
    CONFLICT:      { title: 'Not applied', description: 'Already counting toward a conflicting requirement' },
    OVERLIMIT:     { title: 'Not applied', description: 'Over the course limit for this requirement' },
    INACTIVE:      { title: 'Not applied', description: 'This section is not active' },
    OVERLAP_QUOTA: { title: 'Not applied', description: 'Exceeds the overlap quota with another program' },
    USER_OVERRIDE: { title: 'Not applied', description: 'Manually removed by you' },
    SCORE_NOT_MET: { title: 'Not applied', description: 'Exam score requirement not met' },
};

const representativeStatusPriority: Record<string, number> = {
    COMPLETED: 4,
    IN_PROGRESS: 3,
    PLANNED: 2,
    SAVED: 1,
};

function getCombinedGroupId(course: CourseLite): number | null {
    const combinedGroupId = course.combinedCourseInfo.combinedGroupId;
    return combinedGroupId && combinedGroupId > 0 ? combinedGroupId : null;
}

function compareRepresentativeCandidates(
    a: CourseLite,
    b: CourseLite,
    relevantSubjectsSet: Set<string>
): number {
    const aStatusPriority = representativeStatusPriority[a.userState.status] ?? 0;
    const bStatusPriority = representativeStatusPriority[b.userState.status] ?? 0;
    if (aStatusPriority !== bStatusPriority) {
        return bStatusPriority - aStatusPriority;
    }

    const aIsRelevantSubject = relevantSubjectsSet.has(a.courseInfo.subject);
    const bIsRelevantSubject = relevantSubjectsSet.has(b.courseInfo.subject);
    if (aIsRelevantSubject !== bIsRelevantSubject) {
        return aIsRelevantSubject ? -1 : 1;
    }

    if (a.courseInfo.level !== b.courseInfo.level) {
        return a.courseInfo.level - b.courseInfo.level;
    }

    if (a.courseInfo.subject !== b.courseInfo.subject) {
        return a.courseInfo.subject.localeCompare(b.courseInfo.subject);
    }

    if (a.courseInfo.number !== b.courseInfo.number) {
        return a.courseInfo.number.localeCompare(b.courseInfo.number);
    }

    return a.id.localeCompare(b.id);
}

function selectRepresentativeCourse<T extends CourseLite>(
    courses: T[],
    relevantSubjectsSet: Set<string>,
    includeCompletedRepresentative = false
): T | null {
    if (courses.length === 0) {
        return null;
    }

    const sortedCourses = [...courses].sort((a, b) =>
        compareRepresentativeCandidates(a, b, relevantSubjectsSet)
    );

    const representativeCourse = sortedCourses[0];
    if (!includeCompletedRepresentative && representativeCourse.userState.status === "COMPLETED") {
        return null;
    }

    return representativeCourse;
}

/**
 * 判断一个 requirement 是否已满足
 * 直接使用后端返回的 summary.is_fulfilled
 */
export function isRequirementFulfilled(requirement: Requirement): boolean {
    return requirement.rootNode.summary.isFulfilled;
}

/** 树内任一 COURSE_SET 带内涵谓词即算"内涵 requirement"(任一节点口径,内涵节点可不在根) */
export function hasIntensionalNode(node: Select | CourseSet): boolean {
    if (node.type === "COURSE_SET") return node.query != null;
    return node.children.some(hasIntensionalNode);
}

export type BroadKind = "intensional" | "approval";

/**
 * broad requirement 的两种子形态(契约 §3.0-3):
 *  - intensional:树内有谓词节点。判定由后端物化进分桶,推荐走 suggestions 端点,
 *    渲染两段式("你的课" + 推荐)。
 *  - approval:纯审批型(无谓词、空池)。无判定信息、无推荐,保持"提示 + 搜索自加"。
 * 非 broad requirement 返回 null。
 */
export function getBroadKind(requirement: Requirement): BroadKind | null {
    if (!requirement.info.isBroadRequirement) return null;
    return hasIntensionalNode(requirement.rootNode) ? "intensional" : "approval";
}

/**
 * 递归遍历节点树，收集所有 CourseSet 节点的 summary
 */
export function collectAllCourseSetSummaries(node: Select | CourseSet): CourseSetSummary[] {
    if (node.type === "COURSE_SET") {
        return [node.summary];
    }
    // SELECT node: 递归收集所有子节点的 summary
    return node.children.flatMap(child => collectAllCourseSetSummaries(child));
}

/**
 * 获取一个 requirement 下所有 applied 的课程 ID（按状态分类）
 */
export function getAppliedCourseIds(requirement: Requirement): {
    completedApplied: string[];
    completedUnapplied: string[];
    inProgressApplied: string[];
    inProgressUnapplied: string[];
    plannedApplied: string[];
    plannedUnapplied: string[];
    savedApplied: string[];
    savedUnapplied: string[];
} {
    const summaries = collectAllCourseSetSummaries(requirement.rootNode);
    
    const completedApplied: string[] = [];
    const completedUnapplied: string[] = [];
    const inProgressApplied: string[] = [];
    const inProgressUnapplied: string[] = [];
    const plannedApplied: string[] = [];
    const plannedUnapplied: string[] = [];
    const savedApplied: string[] = [];
    const savedUnapplied: string[] = [];
    
    for (const summary of summaries) {
        completedApplied.push(...summary.completedAppliedCourseIds);
        completedUnapplied.push(...summary.completedUnappliedCourseIds);
        inProgressApplied.push(...summary.inProgressAppliedCourseIds);
        inProgressUnapplied.push(...summary.inProgressUnappliedCourseIds);
        plannedApplied.push(...summary.plannedAppliedCourseIds);
        plannedUnapplied.push(...summary.plannedUnappliedCourseIds);
        savedApplied.push(...summary.savedAppliedCourseIds);
        savedUnapplied.push(...summary.savedUnappliedCourseIds);
    }
    
    return { completedApplied, completedUnapplied, inProgressApplied, inProgressUnapplied, plannedApplied, plannedUnapplied, savedApplied, savedUnapplied };
}

/**
 * 获取已 applied 的单位数量（课程数或学分数，取决于 rule）
 * 直接使用后端返回的 summary.applied_units_count
 */
export function getCompletedUnitsCount(requirement: Requirement): number {
    return requirement.rootNode.summary.completedUnitsCount;
}

/**
 * 获取 requirement 需要的总数量（课程数或学分数，取决于 rule）
 * 从 root_node.rule 中获取
 * Select 节点可能只有 required_children_count（按子节点数），
 * 也可能有 required_units_count（按课程数/学分数）
 */
export function getRequiredUnitsCount(requirement: Requirement): number {
    const rule = requirement.rootNode.rule;
    if (rule.atLeastUnitsCount != null) {
        return rule.atLeastUnitsCount;
    }
    if ('requiredChildrenCount' in rule && rule.requiredChildrenCount != null) {
        return rule.requiredChildrenCount;
    }
    return 0;
}

/**
 * 获取 requirement 的单位类型标签
 * 用于 ReqSectionHeader 显示 "X / Y completed" 或 "X / Y credits"
 */
export function getUnitsLabel(requirement: Requirement): string {
    const rule = requirement.rootNode.rule;
    if (rule.unitsType === "CREDIT") {
        return "credits";
    }
    return "completed";
}

/**
 * 生成 requirement 标题下的"选课规则"描述行,用于在统一的扁平列表前提下
 * 表达"必修(都要上) vs 选修(从一堆里挑几门)"。措辞按 rule 自动推导:
 *   - 按学分:    "Choose N credits"
 *   - 按课程数,且要求数 >= 可选池: "Complete all M courses"
 *   - 按课程数,可选池更大:        "Choose N of M courses"
 *   - 仅按子节点数(且非 option 类,option 类已由 header 的 options 进度表达):
 *                "Choose N of the following"
 * 已满足或无法给出有意义文案时返回 null。
 */
export function getRequirementRuleDescriptor<T extends CourseLite>(
    requirement: Requirement,
    coursesMap: Record<string, T>
): string | null {
    if (isRequirementFulfilled(requirement)) return null;

    const rule = requirement.rootNode.rule;

    // OPTION / OPTION_GROUP:按"选几个 option"表达,额外的 units 要求(若有)附在后面
    if (isOptionLikeRequirement(requirement)) {
        const children = (requirement.rootNode as Select).children;
        const total = children.length;
        const required = ('requiredChildrenCount' in rule && rule.requiredChildrenCount != null)
            ? rule.requiredChildrenCount
            : total;
        if (required <= 0 || total <= 0) return null;
        let descriptor = required >= total
            ? `Complete all of the following options`
            : `Choose ${required} of the following options`;
        if (rule.atLeastUnitsCount != null && rule.atLeastUnitsCount > 0) {
            const n = rule.atLeastUnitsCount;
            const unit = rule.unitsType === "CREDIT"
                ? `credit${n > 1 ? 's' : ''}`
                : `course${n > 1 ? 's' : ''}`;
            descriptor += ` and complete ${n} ${unit}`;
        }
        return descriptor;
    }

    return getNodeRuleDescriptor(requirement.rootNode, coursesMap);
}

/**
 * 节点级别的"选课规则"描述,口径同 getRequirementRuleDescriptor 的非 option 分支。
 * 既用于 requirement 根节点,也用于 OPTION 类 section 里单个 option(course set / group)
 * 的小标题。不含 isFulfilled 判断——是否在已满足时隐藏由调用方决定。
 */
export function getNodeRuleDescriptor<T extends CourseLite>(
    node: Select | CourseSet,
    coursesMap: Record<string, T>
): string | null {
    const rule = node.rule;

    // 可选池:树中去重、确实存在的非考试课程;combined(交叉列出)课程合并计为一门,
    // 与课程行展示口径一致(见 getCoursesFromCourseSet)。
    const seenIds = new Set<string>();
    const seenCombinedGroups = new Set<number>();
    let pool = 0;
    for (const id of collectAllRequiredCourseIds(node)) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        const course = coursesMap[id];
        if (!course || course.type === 'EXAM') continue;
        const combinedGroupId = getCombinedGroupId(course);
        if (combinedGroupId !== null) {
            if (seenCombinedGroups.has(combinedGroupId)) continue;
            seenCombinedGroups.add(combinedGroupId);
        }
        pool++;
    }

    if (rule.atLeastUnitsCount != null) {
        const n = rule.atLeastUnitsCount;
        if (n <= 0) return null;
        if (rule.unitsType === "CREDIT") {
            return `Choose ${n} credit${n > 1 ? 's' : ''}`;
        }
        if (pool >= 2 && n >= pool) {
            return `Complete all ${pool} courses`;
        }
        if (pool > n) {
            return `Choose ${n} of ${pool} courses`;
        }
        return `Choose ${n} course${n > 1 ? 's' : ''}`;
    }

    if ('requiredChildrenCount' in rule && rule.requiredChildrenCount != null) {
        const n = rule.requiredChildrenCount;
        if (n <= 0) return null;
        // GROUP:每组内部还有多门课可选,列表里展示的只是随机抽的代表课,
        // 不是钦定的必修课,所以措辞要强调"每组一门、组内可选",按组数度量。
        const groupCount = (node as Select).children
            .filter(child => child.type === "COURSE_SET").length;
        if (groupCount >= 2 && n >= groupCount) {
            return `Complete 1 course from each of ${groupCount} groups`;
        }
        if (groupCount > n) {
            return `Complete 1 course from any ${n} of ${groupCount} groups`;
        }
        return `Complete 1 course from this group`;
    }

    return null;
}

/**
 * 获取已修完的课程（不含 planned/saved），用于 ReqSectionHeader 展开时显示
 * applied 的排在前面，unapplied 排在后面
 */
export function getCompletedCourseOptions<T extends CourseLite>(
    requirement: Requirement,
    coursesMap: Record<string, T>
): { course: T; isApplied: boolean }[] {
    const { completedApplied, completedUnapplied } = getAppliedCourseIds(requirement);

    const toEntry = (id: string, isApplied: boolean) => {
        const course = coursesMap[id];
        if (!course) return null;
        return { course, isApplied };
    };

    return [
        ...completedApplied.map(id => toEntry(id, true)),
        ...completedUnapplied.map(id => toEntry(id, false)),
    ].filter((entry): entry is { course: T; isApplied: boolean } => entry !== null);
}

/**
 * 获取单个 CourseSet 节点的用户课程（completed / in-progress / planned），applied 排前面，
 * 同 applied 组内按 completed → in-progress → planned 排序，unapplied 同理排后。
 */
export function getUserCourseOptionsForCourseSet<T extends CourseLite>(
    courseSet: CourseSet,
    coursesMap: Record<string, T>
): { course: T; isApplied: boolean }[] {
    const { summary } = courseSet;
    const toEntry = (id: string, isApplied: boolean) => {
        const course = coursesMap[id];
        if (!course) return null;
        return { course, isApplied };
    };

    return [
        ...summary.completedAppliedCourseIds.map(id => toEntry(id, true)),
        ...summary.inProgressAppliedCourseIds.map(id => toEntry(id, true)),
        ...summary.plannedAppliedCourseIds.map(id => toEntry(id, true)),
        ...summary.completedUnappliedCourseIds.map(id => toEntry(id, false)),
        ...summary.inProgressUnappliedCourseIds.map(id => toEntry(id, false)),
        ...summary.plannedUnappliedCourseIds.map(id => toEntry(id, false)),
    ].filter((entry): entry is { course: T; isApplied: boolean } => entry !== null);
}

/**
 * 获取单个 CourseSet 节点的所有课程，并标记哪些已 applied
 */
export function getAllCourseOptionsForCourseSet<T extends CourseLite>(
    courseSet: CourseSet,
    coursesMap: Record<string, T>
): { course: T; isApplied: boolean }[] {
    const { summary } = courseSet;
    const appliedSet = new Set([
        ...summary.completedAppliedCourseIds,
        ...summary.inProgressAppliedCourseIds,
        ...summary.plannedAppliedCourseIds,
    ]);

    return courseSet.requiredCourseIds
        .map(id => {
            const course = coursesMap[id];
            if (!course) return null;
            return { course, isApplied: appliedSet.has(id) };
        })
        .filter((item): item is { course: T; isApplied: boolean } => item !== null);
}

/**
 * 获取单个 CourseSet 节点的 slot 渲染所需的全部信息
 */
export function getCourseSetSlotInfo<T extends CourseLite>(
    courseSet: CourseSet,
    coursesMap: Record<string, T>
): {
    appliedCourses: { course: T; isApplied: true }[];
    unappliedCourses: { course: T; isApplied: false }[];
    allCourses: { course: T; isApplied: boolean }[];
    emptySlotCount: number;
    isFulfilled: boolean;
    label: string | undefined;
} {
    const userCourses = getUserCourseOptionsForCourseSet(courseSet, coursesMap);
    const appliedCourses = userCourses.filter(
        (entry): entry is { course: T; isApplied: true } => entry.isApplied
    );
    const unappliedCourses = userCourses.filter(
        (entry): entry is { course: T; isApplied: false } => !entry.isApplied
    );
    const allCourses = getAllCourseOptionsForCourseSet(courseSet, coursesMap);
    const { rule, summary, title } = courseSet;
    const isFulfilled = summary.isFulfilled;
    const isCredit = rule.unitsType === "CREDIT";
    const requiredCount = rule.atLeastUnitsCount ?? 0;
    const emptySlotCount = isFulfilled
        ? 0
        : isCredit
        ? 1
        : Math.max(0, requiredCount - appliedCourses.length);

    return {
        appliedCourses,
        unappliedCourses,
        allCourses,
        emptySlotCount,
        isFulfilled,
        label: title || undefined,
    };
}

/**
 * 从 CourseSet 节点获取课程列表
 * 用于 panelUtils 的 buildPanelRows(LIST/OPTION 拍平,GROUP/OPTION_GROUP 组代表课)
 */
export function getCoursesFromCourseSet<T extends CourseLite>(
    courseSet: CourseSet,
    coursesMap: Record<string, T>,
    relevantSubjects: string[] = [],
    options?: {
        includeCompletedRepresentative?: boolean;
    }
): T[] {
    const relevantSubjectsSet = new Set(relevantSubjects);
    const combinedCoursesByGroup = new Map<number, T[]>();
    const orderedEntries: ({ type: "standalone"; course: T } | { type: "combined"; groupId: number })[] = [];

    const courses = courseSet.requiredCourseIds
        .map(id => coursesMap[id])
        .filter((course): course is T => course !== undefined);

    for (const course of courses) {
        const combinedGroupId = getCombinedGroupId(course);
        if (combinedGroupId === null) {
            orderedEntries.push({ type: "standalone", course });
            continue;
        }

        if (!combinedCoursesByGroup.has(combinedGroupId)) {
            orderedEntries.push({ type: "combined", groupId: combinedGroupId });
        }
        const groupedCourses = combinedCoursesByGroup.get(combinedGroupId) ?? [];
        groupedCourses.push(course);
        combinedCoursesByGroup.set(combinedGroupId, groupedCourses);
    }

    return orderedEntries.flatMap((entry) => {
        if (entry.type === "standalone") {
            return [entry.course];
        }

        const representativeCourse = selectRepresentativeCourse(
            combinedCoursesByGroup.get(entry.groupId) ?? [],
            relevantSubjectsSet,
            options?.includeCompletedRepresentative ?? false
        );

        return representativeCourse ? [representativeCourse] : [];
    });
}

/**
 * GROUP / OPTION_GROUP 用:把组(CourseSet)拍平成"每组一行槽位"。
 * 每行 = 该组的一门代表课 + 组内可选课数(optionCount,用于 "· N options" 提示)。
 * 不强行展示全部组,沿用预览采样逻辑:
 *   - 跳过已满足、组内无(非考试)课的组;
 *   - 有 available 课(本学期+地点可上)的组排前面,无 available 课的组沉底
 *     (不再丢弃,展开 "Show more groups" 即可看到所有未完成的组);
 *   - 同一档内组顺序按稳定随机键(同 session 一致,刷新重洗,与 LIST 随机一致);
 *   - 每组取一门尚未出现过的代表课(去重,课程可能跨组重复)。
 * 调用方用 slice(0,N) + "Show more groups" 控制展示几个组。
 */
export function getGroupRepresentatives(
    courseSets: CourseSet[],
    coursesMap: Record<string, CourseSummary>,
    relevantSubjects: string[] = []
): { course: CourseSummary; optionCount: number; courseSet: CourseSet }[] {
    const eligibleGroups: { courseSet: CourseSet; courses: CourseSummary[]; hasAvailable: boolean }[] = [];
    for (const courseSet of courseSets) {
        if (courseSet.summary.isFulfilled) continue;
        const courses = getCoursesFromCourseSet(courseSet, coursesMap, relevantSubjects)
            .filter(c => c.type !== 'EXAM');
        if (courses.length === 0) continue;
        const hasAvailable = courses.some(c => c.courseState.isSemesterAvailable && c.courseState.isLocationAvailable);
        eligibleGroups.push({ courseSet, courses: sortCourses(courses), hasAvailable });
    }

    // 有 available 课的组排前面;同档内按稳定随机键
    eligibleGroups.sort((a, b) => {
        if (a.hasAvailable !== b.hasAvailable) return a.hasAvailable ? -1 : 1;
        return getRandomKey(a.courseSet.id) - getRandomKey(b.courseSet.id);
    });

    const seen = new Set<string>();
    const slots: { course: CourseSummary; optionCount: number; courseSet: CourseSet }[] = [];
    for (const group of eligibleGroups) {
        const representative = group.courses.find(c => !seen.has(c.mapKey));
        if (!representative) continue;
        seen.add(representative.mapKey);
        slots.push({ course: representative, optionCount: group.courses.length, courseSet: group.courseSet });
    }
    return slots;
}

/**
 * 递归收集 requirement 树中所有 CourseSet 节点的 requiredCourseIds
 */
export function collectAllRequiredCourseIds(node: Select | CourseSet): string[] {
    if (node.type === "COURSE_SET") {
        return node.requiredCourseIds;
    }
    return node.children.flatMap(child => collectAllRequiredCourseIds(child));
}

/**
 * 获取一个 requirement 下所有课程，并标记哪些已 applied
 * 用于 ReqSectionHeader 展开时的搜索/浏览列表
 */
export function getAllCourseOptionsForRequirement<T extends CourseLite>(
    requirement: Requirement,
    coursesMap: Record<string, T>
): { course: T; isApplied: boolean }[] {
    const allIds = Array.from(new Set(collectAllRequiredCourseIds(requirement.rootNode)));
    const { completedApplied, inProgressApplied, plannedApplied } = getAppliedCourseIds(requirement);
    const appliedSet = new Set([...completedApplied, ...inProgressApplied, ...plannedApplied]);

    return allIds
        .map(id => {
            const course = coursesMap[id];
            if (!course) return null;
            return { course, isApplied: appliedSet.has(id) };
        })
        .filter((item): item is { course: T; isApplied: boolean } => item !== null);
}

/**
 * OPTION 类型：获取 option 级别的进度（已满足的子节点数 / 需要的子节点数）
 */
export function getOptionProgress(requirement: Requirement): { fulfilled: number; required: number } {
    const rootNode = requirement.rootNode as Select;
    const fulfilled = rootNode.fulfilledChildIds.length;
    const required = ('requiredChildrenCount' in rootNode.rule && rootNode.rule.requiredChildrenCount != null)
        ? rootNode.rule.requiredChildrenCount
        : rootNode.children.length;
    return { fulfilled, required };
}

/**
 * OPTION 类型：判断 root 节点是否同时有 units 级别的要求（课程数/学分数）
 */
export function hasUnitsRequirement(requirement: Requirement): boolean {
    return requirement.rootNode.rule.atLeastUnitsCount != null;
}

/**
 * 判断 requirement 是否按 "option 进度"（已满足子节点数 / 需要子节点数）展示，
 * 而不是按 units（课程数/学分数）展示。OPTION 和 OPTION_GROUP 都属于此类。
 */
export function isOptionLikeRequirement(requirement: Requirement): boolean {
    return requirement.info.uiType === "OPTION" || requirement.info.uiType === "OPTION_GROUP";
}

/**
 * 对于一个 CourseSet（group topic），当它要求超过一门课/一个学分时，
 * 返回 "已完成 / 需要" 的进度文案（如 "1 / 2 courses"），用于在 topic 标题旁标注。
 * 只要求一门（或没有有效要求）时返回 null —— 此时不需要额外标注。
 */
export function getCourseSetMultiRequirementLabel(courseSet: CourseSet): string | null {
    const required = courseSet.rule.atLeastUnitsCount;
    if (required == null || required <= 1) return null;
    const completed = courseSet.summary.completedUnitsCount;
    const noun = courseSet.rule.unitsType === "CREDIT" ? "credits" : "courses";
    return `${completed} / ${required} ${noun}`;
}
